/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * <p>
 * Copyright © 2019 Pearson Education, Inc.
 * All Rights Reserved.
 * <p>
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained from Pearson Education, Inc.
 *
 * This is the data class for holding partner attributes.
 *
 * @author Malinda
 * 06/09/2021
 *
 */

import React, {useEffect, useState} from 'react';
import API from "../../../../../utils/api";
import '../formElement.css';
import AsyncPaginate from "react-select-async-paginate";
import './asyncPaginateStyles.css';

/**
 *
 * @param labelName - field label
 * @param valueType - the dropdown type(eg. partner, partnerRoles, basic)
 * @param value - field default value if available
 * @param handleChange
 * @param path - url path for the controller (eg. /partners, /partner-urls)
 * @param optionValueName
 * @param optionLabelName
 * @returns {*}
 * @constructor
 */
const DropdownSelect = ({labelName, valueType, value, handleChange, path, optionValueName, optionLabelName, partnerFilterValue = ''}) => {
    // get user credentials
    const user = JSON.parse(localStorage.getItem('tmcUser'));
    // create token
    const token = 'Bearer ' + user.token;

    // the selected value from the dropdown
    const [selectedValue, setSelectedValue] = useState(null);

    useEffect(() => {
        setSelectedValue(value);
    }, [value]);

    const loadOptions = async (search, loadedOptions, { page }) => {
        // has more data to load
        let hasMore = true;
        // the options in the list
        const options = [];

        if (valueType === 'partners') {
            let url;
            if (search === '') {// check a search or not
                url = process.env.REACT_APP_BASE_URL + path + '/partners?page-no=' + page;
            } else {
                url = process.env.REACT_APP_BASE_URL + path + '/partners?page-no=' + page + '&search=' + search;
            }

            await API.get(url, {headers: {Authorization: token}})
                .then(res => {
                    const partners = Object.values(res.data);
                    partners.forEach(part => {
                        options.push({
                            value: part.partnerId,
                            label: part.partnerName,
                            optionValueName: optionValueName,
                            optionLabelName: optionLabelName
                        })
                    });
                    if (partners.length === 0) {
                        hasMore = false
                    }
                })
                .catch(er => {
                    hasMore = false
                });
        } else if (valueType === 'partnerUrlTypes') {
            let url;
            if (search === '') {// check a search or not
                url = process.env.REACT_APP_BASE_URL + '/partner-urls/partner-url-types?page-no=' + page;
            } else {
                url = process.env.REACT_APP_BASE_URL + path + '/partner-url-types?page-no=' + page + '&search=' + search;
            }

            await API.get(url, {headers: {Authorization: token}})
                .then(res => {
                    const partnerUrlTypes = Object.values(res.data);
                    partnerUrlTypes.forEach(part => {
                        options.push({
                            value: part.partnerUrlTypeId,
                            label: part.partnerUrlTypeName,
                            optionValueName: optionValueName,
                            optionLabelName: optionLabelName
                        })
                    });
                    if (partnerUrlTypes.length === 0) {
                        hasMore = false
                    }
                })
                .catch(er => {
                    hasMore = false
                });
        } else if ((valueType === 'institutions')) {
            let url;
            if (search === '') {// check a search or not
                url = process.env.REACT_APP_BASE_URL + path + '/institutions?page-no=' + page;
            } else {
                url = process.env.REACT_APP_BASE_URL + path + '/institutions?page-no=' + page + '&search=' + search;
            }
            await API.get(url, {headers: {Authorization: token}})
                .then(res => {
                    const institutions = Object.values(res.data);
                    institutions.forEach(part => {
                        const label = part.institutionName + ' - (' + part.partnerName + ')';
                        options.push({
                            value: part.institutionId,
                            label: label,
                            optionValueName: optionValueName,
                            optionLabelName: optionLabelName
                        })
                    });
                    if (institutions.length === 0) {
                        hasMore = false
                    }
                })
                .catch(er => {
                    hasMore = false
                });

        } else if ((valueType === 'lms')) {
            let url;
            if (search === '') {// check a search or not
                url = process.env.REACT_APP_BASE_URL + path + '/lms?page-no=' + page;
            } else {
                url = process.env.REACT_APP_BASE_URL + path + '/lms?page-no=' + page + '&search=' + search;
            }
            await API.get(url, {headers: {Authorization: token}})
                .then(res => {
                    const lmsList = Object.values(res.data);
                    lmsList.forEach(part => {
                        options.push({
                            value: part.lmsId,
                            label: part.lmsName,
                            optionValueName: optionValueName,
                            optionLabelName: optionLabelName
                        })
                    });
                    if (lmsList.length === 0) {
                        hasMore = false
                    }
                })
                .catch(er => {
                    hasMore = false
                });
        }
        else if ((valueType === 'institutionsAll')) {
            let url;
            if (search === '') {// check a search or not
                url = process.env.REACT_APP_BASE_URL + path + '/institutions?page-no=' + page;
            } else {
                url = process.env.REACT_APP_BASE_URL + path + '/institutions?page-no=' + page + '&search=' + search;
            }
            await API.get(url, {headers: {Authorization: token}})
                .then(res => {
                    const institutions = Object.values(res.data);
                    institutions.forEach(part => {
                        options.push({
                            value: part.instituteId,
                            label: part.instituteName,
                            optionValueName: optionValueName,
                            optionLabelName: optionLabelName

                        })
                    });
                    if (institutions.length === 0) {
                        hasMore = false
                    }
                })
                .catch(er => {
                    hasMore = false
                });
        }
        else if (valueType === 'lmsUrlTypes') {
            let url;
            if (search === '') {// check a search or not
                url = process.env.REACT_APP_BASE_URL + '/lms-urls/lms-url-types?page-no=' + page;
            } else {
                url = process.env.REACT_APP_BASE_URL + path + '/lms-url-types?page-no=' + page + '&search=' + search;
            }

            await API.get(url, {headers: {Authorization: token}})
                .then(res => {
                    const lmsurlTypeList = Object.values(res.data);
                    lmsurlTypeList.forEach(part => {
                        options.push({
                            value: part.lmsUrlTypeId,
                            label: part.lmsUrlTypeName,
                            optionValueName: optionValueName,
                            optionLabelName: optionLabelName
                        })
                    });
                    if (lmsurlTypeList.length === 0) {
                        hasMore = false
                    }
                })
                .catch(er => {
                    hasMore = false
                });
        } else if ((valueType === 'partner-roles')) {
            let url;
            if (search === '') {// check a search or not
                url = process.env.REACT_APP_BASE_URL + '/course-roles?page-no=' + page;
            } else {
                url = process.env.REACT_APP_BASE_URL + '/course-roles/partnerRoles?page-no=' + page + '&search=' + search;
            }

            await API.get(url, {headers: {Authorization: token}})
                .then(res => {
                    const courseRoles = Object.values(res.data);
                    courseRoles.forEach(part => {
                        const label = '(' + part.partnerName + ') - ' + part.partnerRoleName;
                        options.push({
                            value: part.partnerRoleId,
                            label: label,
                            optionValueName: optionValueName,
                            optionLabelName: optionLabelName
                        })
                    });
                    if (courseRoles.length === 0) {
                        hasMore = false
                    }
                })
                .catch(er => {
                    hasMore = false
                });
        } else if (valueType === 'pearson-roles') {
            let url;
            if (search === '') {// check a search or not
                url = process.env.REACT_APP_BASE_URL + path + '/pearsonRoles?page-no=' + page;
            } else {
                url = process.env.REACT_APP_BASE_URL + path + '/pearsonRoles?page-no=' + page + '&search=' + search;
            }

            await API.get(url, {headers: {Authorization: token}})
                .then(res => {
                    const pearsonRoles = Object.values(res.data);
                    pearsonRoles.forEach(part => {
                        options.push({
                            value: part.pearsonRoleId,
                            label: part.pearsonRoleName,
                            optionValueName: optionValueName,
                            optionLabelName: optionLabelName
                        })
                    });
                    if (pearsonRoles.length === 0) {
                        hasMore = false
                    }
                })
                .catch(er => {
                    hasMore = false
                });
        } else if (valueType === 'platforms') {
            let url;
            if (search === '') {// check a search or not
                url = process.env.REACT_APP_BASE_URL + path + '/platforms?page-no=' + page;
            } else {
                url = process.env.REACT_APP_BASE_URL + path + '/platforms?page-no=' + page + '&search=' + search;
            }

            await API.get(url, {headers: {Authorization: token}})
                .then(res => {
                    const platforms = Object.values(res.data);
                    platforms.forEach(part => {
                        if ((part.outComesAuditDataSource !== undefined) && (part.outComesAuditDataSource !== null)) {
                            options.push({
                                value: part.outComesAuditDataSource,
                                label: part.outComesAuditDataSource,
                                optionValueName: optionValueName,
                                optionLabelName: optionLabelName
                            })
                        } else if ((part.outComesErrorDataSource !== undefined) && (part.outComesErrorDataSource !== null)) {
                            options.push({
                                value: part.outComesErrorDataSource,
                                label: part.outComesErrorDataSource,
                                optionValueName: optionValueName,
                                optionLabelName: optionLabelName
                            })
                        }
                    });
                    if (platforms.length === 0) {
                        hasMore = false
                    }
                })
                .catch(er => {
                    hasMore = false
                });
        } else if (valueType === 'gradeBookPartners') {
            let url;
            if (search === '') {// check a search or not
                url = process.env.REACT_APP_BASE_URL + path + '/partners?dataType=1&page-no=' + page;
            } else {
                url = process.env.REACT_APP_BASE_URL + path + '/partners?dataType=1&page-no=' + page + '&search=%7BpartnerName:' + search + '%7D';
            }

            await API.get(url, {headers: {Authorization: token}})
                .then(res => {
                    const partners = Object.values(res.data);
                    partners.forEach(part => {
                        options.push({
                            value: part.partnerId,
                            label: part.partnerName,
                            optionValueName: optionValueName,
                            optionLabelName: optionLabelName
                        })
                    });
                    if (partners.length === 0) {
                        hasMore = false
                    }
                })
                .catch(er => {
                    hasMore = false
                });
        } else if ((valueType === 'gradeBookInstitutions')) {
            let url;
            if (search === '') {
                url = process.env.REACT_APP_BASE_URL + path + '/partners-institution?page-no=' + page;
            } else {
                url = process.env.REACT_APP_BASE_URL + path + '/partners-institution?page-no=' + page + '&search=%7BinstitutionName:' + search + '%7D';
            }

            await API.get(url, {headers: {Authorization: token}})
                .then(res => {
                    const institutions = Object.values(res.data);
                    institutions.forEach(part => {
                        const label = part.institutionName + ' - (' + part.partnerName + ')';
                        options.push({
                            value: part.institutionId,
                            label: label,
                            optionValueName: optionValueName,
                            optionLabelName: optionLabelName
                        })
                    });
                    if (institutions.length === 0) {
                        hasMore = false
                    }
                })
                .catch(er => {
                    hasMore = false
                });
        } else if (valueType === 'logTracePartners') {
            let url;
            if (search === '') {// check a search or not
                url = process.env.REACT_APP_BASE_URL + path + '/partners?dataType=0&page-no=' + page;
            } else {
                url = process.env.REACT_APP_BASE_URL + path + '/partners?dataType=0&page-no=' + page + '&search=%7BpartnerName:' + search + '%7D';
            }

            await API.get(url, {headers: {Authorization: token}})
                .then(res => {
                    const partners = Object.values(res.data);
                    partners.forEach(part => {
                        options.push({
                            value: part.partnerId,
                            label: part.partnerName,
                            optionValueName: optionValueName,
                            optionLabelName: optionLabelName
                        })
                    });
                })
                .catch(er => {
                    hasMore = false
                });
        } else if ((valueType === 'logTraceInstitutions')) {
            const partnerQuery = partnerFilterValue
                ? '&partnerId=' + encodeURIComponent(partnerFilterValue)
                : '';
            let url;
            if (search === '') {
                url = process.env.REACT_APP_BASE_URL + path + '/log-institution?page-no=' + page + partnerQuery;
            } else {
                url = process.env.REACT_APP_BASE_URL + path + '/log-institution?page-no=' + page + partnerQuery
                    + '&search=%7BinstitutionName:' + encodeURIComponent(search) + '%7D';
            }

            await API.get(url, {headers: {Authorization: token}})
                .then(res => {
                    const institutions = Object.values(res.data);
                    institutions.forEach(part => {
                        const label = part.institutionName + ' - (' + part.partnerName + ')';
                        options.push({
                            value: part.institutionId,
                            label: label,
                            optionValueName: optionValueName,
                            optionLabelName: optionLabelName
                        })
                    });
                    if (institutions.length === 0) {
                        hasMore = false
                    }
                })
                .catch(er => {
                    hasMore = false
                });
        } else if ((valueType === 'utf8EnabledToolProfile')) {
            let url;
            if (search === '') {
                url = process.env.REACT_APP_BASE_URL + path + '/utf8EnabledToolProfile/allToolProfiles?page-no=' + page;
            } else {
                url = process.env.REACT_APP_BASE_URL + path + '/utf8EnabledToolProfile/allToolProfiles?page-no=' + page + '&search=%7Bcode:' + search + '%7D';
            }

            await API.get(url, {headers: {Authorization: token}})
                .then(res => {
                    const institutions = Object.values(res.data);
                    institutions.forEach(part => {
                        options.push({
                            value: part.toolProfileId,
                            label: part.code,
                            optionValueName: optionValueName,
                            optionLabelName: optionLabelName
                        })
                    });
                    if (institutions.length === 0) {
                        hasMore = false
                    }
                })
                .catch(er => {
                    hasMore = false
                });
        } else if (valueType === 'view-audit-type' || valueType === 'view-error-type') {
            options.push({value: 'createLineItemRequest', label: 'Create LineItem', optionValueName: optionValueName, optionLabelName: optionLabelName})
            options.push({value: 'createResultRequest', label: 'Create Results', optionValueName: optionValueName, optionLabelName: optionLabelName})
            options.push({value: 'deleteLineItemRequest', label: 'Delete LineItem', optionValueName: optionValueName, optionLabelName: optionLabelName})
            options.push({value: 'replaceLineItemRequest', label: 'Replace LineItem', optionValueName: optionValueName, optionLabelName: optionLabelName})
            options.push({value: 'updateLineItemRequest', label: 'Update LineItem', optionValueName: optionValueName, optionLabelName: optionLabelName})
            options.push({value: 'deleteResultRequest', label: 'Delete Result', optionValueName: optionValueName, optionLabelName: optionLabelName})
            options.push({value: 'replaceResultRequest', label: 'Replace Result', optionValueName: optionValueName, optionLabelName: optionLabelName})
            options.push({value: 'updateResultRequest', label: 'Update Result', optionValueName: optionValueName, optionLabelName: optionLabelName})
            hasMore = false;
        } else if (valueType === 'view-audit-status') {
            options.push({value: 'success', label: 'Success', optionValueName: optionValueName, optionLabelName: optionLabelName})
            options.push({value: 'error_parsing', label: 'ErrorParsing', optionValueName: optionValueName, optionLabelName: optionLabelName})
            options.push({value: 'error_sending', label: 'Error Sending', optionValueName: optionValueName, optionLabelName: optionLabelName})
            options.push({value: 'Missing Message', label: 'Missing Message', optionValueName: optionValueName, optionLabelName: optionLabelName})
            options.push({value: 'max_send_exceeded', label: 'Send Exceeded', optionValueName: optionValueName, optionLabelName: optionLabelName})
            options.push({value: 'retrying', label: 'Retrying', optionValueName: optionValueName, optionLabelName: optionLabelName})
            options.push({value: 'received', label: 'Received', optionValueName: optionValueName, optionLabelName: optionLabelName})
            options.push({value: 'sending', label: 'Sending', optionValueName: optionValueName, optionLabelName: optionLabelName})
            hasMore = false;
        } else if (valueType === 'view-error-status') {
            options.push({value: 'error_receiving', label: 'ErrorReceiving', optionValueName: optionValueName, optionLabelName: optionLabelName})
            options.push({value: 'error_parsing', label: 'ErrorParsing', optionValueName: optionValueName, optionLabelName: optionLabelName})
            options.push({value: 'error_sending', label: 'Error Sending', optionValueName: optionValueName, optionLabelName: optionLabelName})
            options.push({value: 'Missing Message', label: 'Missing Message', optionValueName: optionValueName, optionLabelName: optionLabelName})
            options.push({value: 'max_send_exceeded', label: 'Send Exceeded', optionValueName: optionValueName, optionLabelName: optionLabelName})
            options.push({value: 'retrying', label: 'Retrying', optionValueName: optionValueName, optionLabelName: optionLabelName})
            options.push({value: 'received', label: 'Received', optionValueName: optionValueName, optionLabelName: optionLabelName})
            options.push({value: 'not_sendable', label: 'Not Sendable', optionValueName: optionValueName, optionLabelName: optionLabelName})
            hasMore = false;
        } else if (valueType === 'log-level') {
            options.push({value: 'ERROR', label: 'ERROR', optionValueName: optionValueName, optionLabelName: optionLabelName})
            options.push({value: 'INFO', label: 'INFO', optionValueName: optionValueName, optionLabelName: optionLabelName})
            options.push({value: 'WARN', label: 'WARN', optionValueName: optionValueName, optionLabelName: optionLabelName})
            hasMore = false;
        } else if (valueType === 'tool-provider-status') {
            if (!value.value) {
                options.push({value: true, label: 'Running', optionValueName: optionValueName, optionLabelName: optionLabelName });
            }
            hasMore = false;
        } else if (valueType === 'logType') {
            options.push({value: 'coreLogsTrace', label: 'Core Logs', optionValueName: optionValueName, optionLabelName: optionLabelName})
            options.push({value: 'outComesAuditLogs', label: 'Outcomes Audit', optionValueName: optionValueName, optionLabelName: optionLabelName})
            options.push({value: 'outComesErrorLogs', label: 'Outcomes Error', optionValueName: optionValueName, optionLabelName: optionLabelName})
            hasMore = false;
        } else {
            options.push({value: true, label: 'Yes', optionValueName: optionValueName, optionLabelName: optionLabelName });
            options.push({value: false, label: 'No', optionValueName: optionValueName, optionLabelName: optionLabelName});
            hasMore = false;
        }

        return {
            options: options,
            hasMore,

            additional: {
                page: page + 1
            }
        }
    };

    const setSelectedOption = selectedOption => {
        handleChange(selectedOption);
        setSelectedValue(selectedOption);
    };

    return(
        <div>
            <label>
                {labelName}
            </label>
            <AsyncPaginate
                value={selectedValue}
                loadOptions={loadOptions}
                onChange={setSelectedOption}
                additional={{
                    page: 0,
                }}
            />
        </div>
    );
};

export default DropdownSelect;
